<template>
  <div class="canvas-calendar" :class="{ compact: !zoom && !layouts }">
    <!-- A display-only planner, like the dashboard's calendar widget: no
         event editing, no drag-to-create, no task drops; double-clicking an
         event opens it, and right-clicking one opens the context menu
         (deliberate actions are fine — only grid edits are off).
         The header is embedded inline (the canvas owns the
         window top bar) and the left pane is never shown — the widget's
         config drives everything else. Display preferences (timezones, zoom
         level) are borrowed from the main /calendar route, while layout and
         pane state persist per widget through `storage-key`.

         The canvas's own date picker is the source of the displayed date
         (the `date` prop), so the planner's date display and arrows are
         hidden — they would fight the controlling picker. -->
    <Planner
      embedded
      read-only
      allow-context-menu
      instance-route="/calendar"
      :storage-key="storagePrefix"
      :date="date"
      :default-layout="layout"
      :display="{
        timezones,
        zoom,
        layouts,
        footer,
        date: false,
        sidebar: false,
      }"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";

import Planner from "@/components/planner/Planner.vue";

import type { CanvasCalendarLayout } from "./body";

/**
 * Canvas content widget: an embedded planner calendar. All props mirror the
 * `CanvasCalendarWidget` body fields (see `body.ts`), resolved to their
 * defaults by the caller.
 */
export default defineComponent({
  name: "CanvasCalendarWidget",
  components: {
    Planner,
  },
  props: {
    /** The displayed date, driven by the canvas's date picker. */
    date: { type: Date, required: true },
    /** Default planner layout. */
    layout: {
      type: String as PropType<CanvasCalendarLayout>,
      required: true,
    },
    /** Show the multi-timezone axis. */
    timezones: { type: Boolean, required: true },
    /** Show the zoom in/out buttons. */
    zoom: { type: Boolean, required: true },
    /** Show the layout selector buttons. */
    layouts: { type: Boolean, required: true },
    /** Show the per-day task footer. */
    footer: { type: Boolean, required: true },
    /** Per-widget key the planner persists its view state under. */
    storagePrefix: { type: String, required: true },
  },
});
</script>

<style scoped lang="scss">
.canvas-calendar {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  overflow: hidden;

  // The planner sizes itself against the viewport (`max-height: 100vh`);
  // inside the widget it must fill the slot instead.
  :deep(.planner) {
    flex: 1;
    min-width: 0;
    min-height: 0;
    max-height: 100%;
  }

  // In the main window the calendar header teleports into the fixed titlebar
  // and floats over the top of the day-header row. The embedded header is
  // floated over that same area instead (same treatment as the dashboard's
  // calendar widget), keeping the grid layout untouched.
  :deep(.calendar .topbar) {
    position: absolute;
    top: 0;
    inset-inline-start: 0;
    inset-inline-end: 0;
    z-index: $z-index-pane-header;
  }

  // With every header control hidden (the date navigation always is — the
  // canvas date picker drives the date — and zoom/layout buttons off),
  // nothing floats over the header area: hide the empty bar (its opaque
  // background would cover the day labels), collapse the day-header row's
  // reserved 100px to a plain label row, and drop the month view's
  // top-bar margin.
  &.compact {
    :deep(.calendar .topbar) {
      display: none;
    }

    :deep(.calendar.isWeekView .fc th.fc-col-header-cell.fc-day) {
      height: auto;
      padding-top: 4px;
    }

    :deep(.days-wrapper.isMonthView) {
      margin-top: 0;
    }
  }
}
</style>
